import { characterRaces } from 'constants/filters/Character';
import { regionItems } from 'constants/filters/Lands';
import { NFTInterface, INFTSale } from 'models/NFT';
import {
  DEMO_WALLET_COLLECTOR,
  DEMO_WALLET_TRADER,
  MOCK_BID_TOKEN,
  MOCK_HRC1155,
  MOCK_HRC721,
  demoImage,
  futureDate,
  pastDate,
  toTokenAmount,
} from './constants';

const characterRacesList = characterRaces.map(r => r.type);
const regions = regionItems.map(r => r.text);
const genders = ['Male', 'Female'];

let saleIdCounter = 1000;

const createSale = (
  type: 'fixed-price' | 'auction',
  price: number,
  seller: string,
  tokenIds: number[],
  status = 'live',
): INFTSale => {
  saleIdCounter += 1;
  const tokenPrice = toTokenAmount(price);
  return {
    bidToken: MOCK_BID_TOKEN,
    saleId: saleIdCounter,
    contractAddress: MOCK_HRC721,
    duration: 604800,
    extensionDuration: 86400,
    isBundle: false,
    minPrice: type === 'auction' ? toTokenAmount(price * 0.5) : '0',
    price: type === 'fixed-price' ? tokenPrice : tokenPrice,
    sortPrice: price,
    status,
    startTime: pastDate(2),
    endTime: futureDate(type === 'auction' ? 5 : 14),
    type,
    buy:
      type === 'auction' && price > 2
        ? { amount: toTokenAmount(price * 0.8), time: pastDate(1), wallet: DEMO_WALLET_TRADER }
        : { amount: '0', time: '', wallet: '' },
    seller,
    tokenIds,
  };
};

const buildCharacterNfts = (): NFTInterface[] =>
  Array.from({ length: 12 }, (_, index) => {
    const tokenId = index + 1;
    const race = characterRacesList[index % characterRacesList.length];
    const gender = genders[index % genders.length];
    const owner = index % 3 === 0 ? DEMO_WALLET_COLLECTOR : DEMO_WALLET_TRADER;
    const forSale = index % 2 === 0;
    const saleType = index % 4 === 0 ? 'auction' : 'fixed-price';
    const price = 1.5 + index * 0.75;

    return {
      _id: `character-${tokenId}`,
      category: 'character',
      contract: MOCK_HRC721,
      subCategory: race,
      owner,
      type: 'ERC721',
      tokenId,
      rarity: { rank: 100 + tokenId, categoryRank: 10 + (index % 5) },
      attributes: {
        GENDER: gender,
        EYES: ['Blue', 'Green', 'Brown', 'Gold'][index % 4],
        HAIR: ['Black', 'Blonde', 'Red', 'Silver'][index % 4],
      },
      metadata: {
        name: `${race.slice(0, -1)} #${tokenId}`,
        image: demoImage(`character-${tokenId}`),
        attributes: [
          { trait_type: 'Race', value: race },
          { trait_type: 'Gender', value: gender },
        ],
      },
      sale: forSale ? createSale(saleType, price, owner, [tokenId]) : (null as unknown as INFTSale),
    };
  });

const buildCompanionNfts = (): NFTInterface[] =>
  Array.from({ length: 10 }, (_, index) => {
    const tokenId = 100 + index + 1;
    const owner = index % 2 === 0 ? DEMO_WALLET_TRADER : DEMO_WALLET_COLLECTOR;
    const types = ['Dragon', 'Phoenix', 'Wolf', 'Owl'];
    const type = types[index % types.length];

    return {
      _id: `companion-${tokenId}`,
      category: 'companion',
      contract: MOCK_HRC721,
      subCategory: type,
      owner,
      type: 'ERC721',
      tokenId,
      rarity: { rank: 200 + tokenId, categoryRank: 20 + (index % 4) },
      attributes: {
        TYPE: type,
        RARITY: ['Common', 'Rare', 'Epic', 'Legendary'][index % 4],
      },
      metadata: {
        name: `${type} Companion #${tokenId}`,
        image: demoImage(`companion-${tokenId}`),
        attributes: [{ trait_type: 'Type', value: type }],
      },
      sale:
        index % 3 !== 2
          ? createSale(index % 2 === 0 ? 'fixed-price' : 'auction', 2 + index * 0.5, owner, [
              tokenId,
            ])
          : (null as unknown as INFTSale),
    };
  });

const buildLandNfts = (): NFTInterface[] =>
  Array.from({ length: 16 }, (_, index) => {
    const tokenId = 200 + index + 1;
    const region = regions[index % regions.length];
    const x = (index % 4) * 10 + 5;
    const y = Math.floor(index / 4) * 10 + 5;
    const owner = index % 3 === 0 ? DEMO_WALLET_COLLECTOR : DEMO_WALLET_TRADER;

    return {
      _id: `land-${tokenId}`,
      category: 'land',
      contract: MOCK_HRC721,
      subCategory: 'Plot',
      owner,
      type: 'ERC721',
      tokenId,
      rarity: null,
      attributes: {
        REGION: region,
        COORDINATES: `${x}, ${y}`,
      },
      metadata: {
        name: `Land Plot #${tokenId}`,
        image: demoImage(`land-${tokenId}`),
        attributes: [
          { trait_type: 'Region', value: region },
          { trait_type: 'Coordinates', value: `${x}, ${y}` },
        ],
      },
      sale:
        index % 2 === 0
          ? createSale('fixed-price', 5 + index * 1.25, owner, [tokenId])
          : (null as unknown as INFTSale),
    };
  });

const buildItemNfts = (): NFTInterface[] =>
  Array.from({ length: 10 }, (_, index) => {
    const tokenId = 300 + index + 1;
    const owner = DEMO_WALLET_COLLECTOR;
    const itemTypes = ['Weapon', 'Armor', 'Accessory'];

    return {
      _id: `item-${tokenId}`,
      category: 'item',
      contract: MOCK_HRC1155,
      subCategory: itemTypes[index % itemTypes.length],
      owner,
      type: 'ERC1155',
      tokenId,
      rarity: { rank: 300 + tokenId, categoryRank: 5 + (index % 3) },
      attributes: {
        TYPE: itemTypes[index % itemTypes.length],
        RARITY: ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'][index % 5],
      },
      metadata: {
        name: `${itemTypes[index % itemTypes.length]} #${tokenId}`,
        image: demoImage(`item-${tokenId}`),
        attributes: [],
      },
      sale:
        index % 2 === 0
          ? createSale('fixed-price', 0.5 + index * 0.3, owner, [tokenId])
          : (null as unknown as INFTSale),
    };
  });

const buildResourceNfts = (): NFTInterface[] =>
  Array.from({ length: 8 }, (_, index) => {
    const tokenId = 400 + index + 1;
    const resourceTypes = ['Ore', 'Wood', 'Crystal', 'Herb'];
    const owner = DEMO_WALLET_TRADER;

    return {
      _id: `resource-${tokenId}`,
      category: 'resource',
      contract: MOCK_HRC1155,
      subCategory: resourceTypes[index % resourceTypes.length],
      owner,
      type: 'ERC1155',
      tokenId,
      rarity: null,
      attributes: { TYPE: resourceTypes[index % resourceTypes.length] },
      metadata: {
        name: `${resourceTypes[index % resourceTypes.length]} Bundle #${tokenId}`,
        image: demoImage(`resource-${tokenId}`),
        attributes: [],
      },
      sale:
        index % 2 === 0
          ? createSale('fixed-price', 0.25 + index * 0.1, owner, [tokenId])
          : (null as unknown as INFTSale),
    };
  });

const buildStructureNfts = (): NFTInterface[] =>
  Array.from({ length: 8 }, (_, index) => {
    const tokenId = 500 + index + 1;
    const structureTypes = ['Tower', 'Shrine', 'Portal', 'Workshop'];
    const owner = index % 2 === 0 ? DEMO_WALLET_COLLECTOR : DEMO_WALLET_TRADER;

    return {
      _id: `structure-${tokenId}`,
      category: 'structure',
      contract: MOCK_HRC1155,
      subCategory: structureTypes[index % structureTypes.length],
      owner,
      type: 'ERC1155',
      tokenId,
      rarity: null,
      attributes: { TYPE: structureTypes[index % structureTypes.length] },
      metadata: {
        name: `${structureTypes[index % structureTypes.length]} #${tokenId}`,
        image: demoImage(`structure-${tokenId}`),
        attributes: [],
      },
      sale:
        index % 3 !== 0
          ? createSale(index % 2 === 0 ? 'auction' : 'fixed-price', 3 + index * 0.8, owner, [
              tokenId,
            ])
          : (null as unknown as INFTSale),
    };
  });

const nfts: NFTInterface[] = [
  ...buildCharacterNfts(),
  ...buildCompanionNfts(),
  ...buildLandNfts(),
  ...buildItemNfts(),
  ...buildResourceNfts(),
  ...buildStructureNfts(),
];

export default nfts;
